import { useEffect, useState } from "react";
import { useAccount } from '../../context/AccountContext';
import ThresholdsTable from "../tables/ThresholdsTable";
import ThresholdModal from "../modals/ThresholdModal";
import {
  listThresholds,
  createThreshold,
  updateThreshold,
  deleteThreshold,
  createDefaultThresholds,
} from "../api/threshold";
import { listEntities } from "../api/entity";
import { listSubEntities } from "../api/subEntity";
import { MdAdd, MdRefresh } from "react-icons/md";
import { useSelector } from "react-redux";

export default function Thresholds() {
  const { accountId } = useSelector((state) => state.account);
    const [thresholds, setThresholds] = useState([]);
    const [modal, setModal] = useState({ open: false, initial: null });
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
  
    const refresh = async () => {
      try {
        setLoading(true);
        const data = await listThresholds(accountId);
        setThresholds(data);
      } catch (err) {
        // console.error("Error loading thresholds:", err);
        setError("Failed to load thresholds");
      } finally {
        setLoading(false);
      }
    };
  
    useEffect(() => {
      if (accountId) {
        refresh();
      }
    }, [accountId]);
  
    const handleSave = async (data) => {
      if (!data) return setModal({ open: false, initial: null });
      try {
        modal.initial
          ? await updateThreshold(modal.initial.id, data)
          : await createThreshold(data);
        setModal({ open: false, initial: null });
        refresh();
      } catch (err) {
        console.error("Error saving threshold:", err);
      }
    };
  
    const handleDelete = async (id) => {
      try {
        await deleteThreshold(id);
        refresh();
      } catch (err) {
        console.error("Error deleting threshold:", err);
      }
    };

    const handleUseDefaultThresholds = async () => {
      try {
        setLoading(true);
        setError(null);
        await createDefaultThresholds(accountId);
        await refresh();
      } catch (err) {
        console.error("Error creating default thresholds:", err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
  
    return (
      <div className="max-w-4xl mx-auto">
        <div className="bg-white border border-gray-200 rounded-md shadow-sm p-4">
          <div className="flex justify-between items-center mb-6">
            <div>
              <h1 className="text-xl font-bold text-gray-900">Thresholds</h1>
              {!loading && (
                <p className="text-sm text-gray-500 mt-1">
                  {thresholds.length} threshold{thresholds.length !== 1 ? "s" : ""} total
                </p>
              )}
            </div>
            <div className="flex gap-2">
              <button
                onClick={refresh}
                disabled={loading}
                className="inline-flex items-center gap-1 px-3 py-1.5 text-gray-700 bg-white border border-gray-200 rounded-md hover:bg-gray-50 transition-colors"
                title="Refresh"
              >
                <MdRefresh className={`${loading ? "animate-spin" : ""}`} />
              </button>
              <button
                className="inline-flex items-center gap-1 px-4 py-1.5 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors disabled:opacity-50"
                onClick={() => setModal({ open: true, initial: null })}
                disabled={loading}
              >
                <MdAdd />
                New
              </button>
            </div>
          </div>

          {error && (
            <div className="mb-4 p-3 text-sm text-red-600 bg-red-50 rounded-md border border-red-100">
              {error}
            </div>
          )}

          {loading && !thresholds.length ? (
            <div className="text-center py-12 text-gray-500">Loading thresholds...</div>
          ) : !thresholds.length ? (
            <div className="text-center py-4">
              <div className="max-w-md mx-auto">
                <h3 className="text-sm font-semibold text-gray-900 mb-1">
                  No Thresholds Set Up Yet
                </h3>
                <p className="text-xs text-gray-500 mb-3">
                  Get started by using our default thresholds or create your own custom thresholds.
                </p>

                <div className="mb-4 text-left bg-gray-50 p-3 rounded-lg">
                  <div className="text-[11px] font-medium text-gray-600 mb-2">Common examples:</div>
                  <ul className="space-y-1.5 text-[11px] text-gray-600 list-inside">
                    <li className="flex items-start gap-2">
                      <span className="w-1.5 h-1.5 bg-red-200 rounded-full shrink-0 mt-1.5"></span>
                      <div>
                        <span className="font-medium text-gray-700">High Risk</span>
                        <span className="text-gray-500"> • Score below -30</span>
                      </div>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="w-1.5 h-1.5 bg-yellow-200 rounded-full shrink-0 mt-1.5"></span>
                      <div>
                        <span className="font-medium text-gray-700">Medium Risk</span>
                        <span className="text-gray-500"> • Score -30 to -11</span>
                      </div>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="w-1.5 h-1.5 bg-green-200 rounded-full shrink-0 mt-1.5"></span>
                      <div>
                        <span className="font-medium text-gray-700">Low Risk</span>
                        <span className="text-gray-500"> • Score -10 or higher</span>
                      </div>
                    </li>
                  </ul>
                </div>

                <div className="flex gap-2 justify-center">
                  <button
                    onClick={handleUseDefaultThresholds}
                    className="px-2.5 py-1 bg-blue-600 text-white text-xs font-medium rounded-md hover:bg-blue-700 transition-colors"
                  >
                    Use Default
                  </button>
                  <span className="text-[11px] text-gray-400 flex items-center">or</span>
                  <button
                    onClick={() => setModal({ open: true, initial: null })}
                    className="px-2.5 py-1 border border-gray-200 text-gray-600 text-xs font-medium rounded-md hover:bg-gray-50 transition-colors"
                  >
                    Create Custom
                  </button>
                </div>
              </div>
            </div>
          ) : (
            <ThresholdsTable
              thresholds={thresholds}
              onEdit={(t) => setModal({ open: true, initial: t })}
              onDelete={handleDelete}
            />
          )}

          <ThresholdModal
            open={modal.open}
            initial={modal.initial}
            account_id={accountId}
            onClose={handleSave}
            isLoading={loading}
          />
        </div>
      </div>
    );
}